import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, query, where, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface ReportCardProps {
  domain: string;
}

interface ExamResult {
  examId: string;
  examName: string;
  examType: string;
  academicYear: string;
  className: string;
  section: string;
  term: string;
  subjects: string[];
  totalMarks: number;
  published: boolean;
}

interface StudentResult {
  studentId: string;
  studentName: string;
  rollNo: string;
  subjects: { [key: string]: { subjectName: string; scored: number; total: number; grade: string } };
  totalScored: number;
  totalMarks: number;
  percentage: number;
  rank: number;
  overallGrade: string;
  attendance: string;
}

interface SubjectConfig {
  id: string;
  subjectName: string;
  subjectType: string;
  className: string;
  totalMarks: number;
  passingMarks: number;
  isActive: boolean;
}

interface SubjectGroup {
  id: string;
  groupName: string;
  subjects: string[];
  bestOf: number; // 0 means take all, >0 means best of N
  displayName: string;
}

interface ReportCardConfig {
  id: string;
  className: string;
  academicYear: string;
  subjectGroups: SubjectGroup[];
  selectedExams: string[]; // examIds to include
  selectedExamWeights: { [examId: string]: number }; // weightage per exam
  published: boolean;
  createdAt: string;
}

interface ComputedStudentReport {
  studentId: string;
  studentName: string;
  rollNo: string;
  section: string;
  examResults: {
    examId: string;
    examName: string;
    subjects: { [subjectName: string]: { scored: number; total: number; grade: string } };
  }[];
  // Combined results
  subjectTotals: {
    subjectName: string;
    weightedScore: number;
    weightedTotal: number;
    percentage: number;
    grade: string;
  }[];
  groupResults: {
    groupName: string;
    displayName: string;
    subjects: {
      subjectName: string;
      weightedScore: number;
      weightedTotal: number;
      selected: boolean; // true if included in best-of
    }[];
    bestOf: number;
    totalScore: number;
    totalMax: number;
    percentage: number;
    grade: string;
  }[];
  grandTotal: number;
  grandMax: number;
  grandPercentage: number;
  overallGrade: string;
  rank: number;
  result: 'Pass' | 'Fail';
}

const ReportCard: React.FC<ReportCardProps> = ({ domain }) => {
  // Config state
  const [className, setClassName] = useState('10');
  const [section, setSection] = useState('A');
  const [academicYear, setAcademicYear] = useState('2025-2026');

  // Data
  const [exams, setExams] = useState<ExamResult[]>([]);
  const [subjects, setSubjects] = useState<SubjectConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);

  // Report card configuration
  const [selectedExams, setSelectedExams] = useState<string[]>([]);
  const [examWeights, setExamWeights] = useState<{ [examId: string]: number }>({});
  const [subjectGroups, setSubjectGroups] = useState<SubjectGroup[]>([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SubjectGroup | null>(null);

  // Group form
  const [groupForm, setGroupForm] = useState({
    groupName: '',
    displayName: '',
    subjects: [] as string[],
    bestOf: 0,
  });

  // Computed reports
  const [studentReports, setStudentReports] = useState<ComputedStudentReport[]>([]);
  const [expandedStudent, setExpandedStudent] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'config' | 'preview'>('config');
  const [savedConfig, setSavedConfig] = useState<ReportCardConfig | null>(null);
  const [publishing, setPublishing] = useState(false);

  const academicYears = ['2024-2025', '2025-2026', '2026-2027'];

  // Load exams and subjects when config changes
  useEffect(() => {
    if (domain) {
      loadData();
    }
  }, [domain, className, section, academicYear]);

  // Load saved config
  useEffect(() => {
    if (domain && className && academicYear) {
      loadSavedConfig();
    }
  }, [domain, className, academicYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load exams for this class/section/year
      const examsRef = collection(db, `school-data/${domain}/exam-results`);
      const examsQuery = query(
        examsRef,
        where('className', '==', className),
        where('section', '==', section),
        where('academicYear', '==', academicYear)
      );
      const examsSnapshot = await getDocs(examsQuery);
      const examsList: ExamResult[] = examsSnapshot.docs.map(d => ({ ...d.data(), examId: d.id } as ExamResult));
      setExams(examsList);

      // Load subjects for this class
      const subjectsRef = collection(db, `school-data/${domain}/subjects`);
      const subjectsSnapshot = await getDocs(subjectsRef);
      const subjectsList: SubjectConfig[] = [];
      subjectsSnapshot.forEach(d => {
        const data = d.data() as SubjectConfig;
        if (String(data.className) === className && data.isActive) {
          subjectsList.push({ ...data, id: d.id });
        }
      });
      subjectsList.sort((a, b) => a.subjectName.localeCompare(b.subjectName));
      setSubjects(subjectsList);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedConfig = async () => {
    try {
      const configId = `rc_${className}_${academicYear.replace('-', '_')}`;
      const configRef = doc(db, `school-data/${domain}/report-card-configs`, configId);
      const configSnap = await getDoc(configRef);
      if (configSnap.exists()) {
        const config = configSnap.data() as ReportCardConfig;
        setSavedConfig(config);
        setSelectedExams(config.selectedExams || []);
        setExamWeights(config.selectedExamWeights || {});
        setSubjectGroups(config.subjectGroups || []);
      } else {
        setSavedConfig(null);
      }
    } catch (error) {
      console.error('Error loading saved config:', error);
    }
  };

  // All unique subject names from selected exams
  const allSubjectNames = useMemo(() => {
    const names = new Set<string>();
    exams
      .filter(e => selectedExams.includes(e.examId))
      .forEach(e => e.subjects?.forEach(s => names.add(s)));
    // Also from subject config
    subjects.forEach(s => names.add(s.subjectName));
    return [...names].sort();
  }, [exams, selectedExams, subjects]);

  // Subjects not assigned to any group
  const ungroupedSubjects = useMemo(() => {
    const grouped = new Set(subjectGroups.flatMap(g => g.subjects));
    return allSubjectNames.filter(s => !grouped.has(s));
  }, [allSubjectNames, subjectGroups]);

  const calculateGrade = (percentage: number): string => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C+';
    if (percentage >= 40) return 'C';
    if (percentage >= 33) return 'D';
    return 'F';
  };

  // Toggle exam selection
  const toggleExam = (examId: string) => {
    setSelectedExams(prev => {
      if (prev.includes(examId)) {
        const newWeights = { ...examWeights };
        delete newWeights[examId];
        setExamWeights(newWeights);
        return prev.filter(id => id !== examId);
      }
      setExamWeights(prev2 => ({ ...prev2, [examId]: 100 }));
      return [...prev, examId];
    });
  };

  // Subject group CRUD
  const openGroupModal = (group?: SubjectGroup) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({
        groupName: group.groupName,
        displayName: group.displayName,
        subjects: [...group.subjects],
        bestOf: group.bestOf,
      });
    } else {
      setEditingGroup(null);
      setGroupForm({ groupName: '', displayName: '', subjects: [], bestOf: 0 });
    }
    setShowGroupModal(true);
  };

  const saveGroup = () => {
    if (!groupForm.groupName.trim()) return;

    const group: SubjectGroup = {
      id: editingGroup?.id || `grp_${Date.now()}`,
      groupName: groupForm.groupName.trim(),
      displayName: groupForm.displayName.trim() || groupForm.groupName.trim(),
      subjects: groupForm.subjects,
      bestOf: groupForm.bestOf,
    };

    if (editingGroup) {
      setSubjectGroups(prev => prev.map(g => g.id === editingGroup.id ? group : g));
    } else {
      setSubjectGroups(prev => [...prev, group]);
    }

    setShowGroupModal(false);
    setEditingGroup(null);
  };

  const deleteGroup = (groupId: string) => {
    setSubjectGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const toggleGroupSubject = (subjectName: string) => {
    setGroupForm(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subjectName)
        ? prev.subjects.filter(s => s !== subjectName)
        : [...prev.subjects, subjectName],
    }));
  };

  // Save configuration to Firestore
  const saveConfig = async () => {
    try {
      const configId = `rc_${className}_${academicYear.replace('-', '_')}`;
      const config: ReportCardConfig = {
        id: configId,
        className,
        academicYear,
        subjectGroups,
        selectedExams,
        selectedExamWeights: examWeights,
        published: savedConfig?.published || false,
        createdAt: savedConfig?.createdAt || new Date().toISOString(),
      };

      await setDoc(doc(db, `school-data/${domain}/report-card-configs`, configId), config);
      setSavedConfig(config);
      alert('Report card configuration saved!');
    } catch (error: any) {
      console.error('Error saving config:', error);
      alert(`Failed to save: ${error.message}`);
    }
  };

  // Compute report cards
  const computeReports = useCallback(async () => {
    if (selectedExams.length === 0) {
      alert('Please select at least one exam.');
      return;
    }

    setComputing(true);
    try {
      // Fetch all student results for selected exams
      const allStudentResults = new Map<string, { studentId: string; studentName: string; rollNo: string; section: string; examResults: Map<string, { [subj: string]: { scored: number; total: number; grade: string } }> }>();

      for (const examId of selectedExams) {
        const studentResultsRef = collection(db, `school-data/${domain}/exam-results/${examId}/student-results`);
        const snapshot = await getDocs(studentResultsRef);

        snapshot.forEach(d => {
          const data = d.data() as StudentResult;
          if (!allStudentResults.has(data.studentId)) {
            allStudentResults.set(data.studentId, {
              studentId: data.studentId,
              studentName: data.studentName,
              rollNo: data.rollNo,
              section: section,
              examResults: new Map(),
            });
          }
          const student = allStudentResults.get(data.studentId)!;
          student.examResults.set(examId, data.subjects || {});
        });
      }

      // Get passing marks per subject
      const passingMarksMap = new Map<string, number>();
      subjects.forEach(s => passingMarksMap.set(s.subjectName, s.passingMarks || 33));

      // Compute weighted results for each student
      const totalWeight = selectedExams.reduce((sum, eid) => sum + (examWeights[eid] || 100), 0);

      const computedReports: ComputedStudentReport[] = [];

      allStudentResults.forEach((student) => {
        // Step 1: Compute weighted score per subject across exams
        const subjectTotalsMap = new Map<string, { weightedScore: number; weightedTotal: number }>();

        student.examResults.forEach((subjects, examId) => {
          const weight = examWeights[examId] || 100;
          Object.entries(subjects).forEach(([subjectName, marks]) => {
            if (!subjectTotalsMap.has(subjectName)) {
              subjectTotalsMap.set(subjectName, { weightedScore: 0, weightedTotal: 0 });
            }
            const entry = subjectTotalsMap.get(subjectName)!;
            entry.weightedScore += (marks.scored * weight) / totalWeight;
            entry.weightedTotal += (marks.total * weight) / totalWeight;
          });
        });

        const subjectTotals = [...subjectTotalsMap.entries()].map(([subjectName, data]) => {
          const percentage = data.weightedTotal > 0 ? (data.weightedScore / data.weightedTotal) * 100 : 0;
          return {
            subjectName,
            weightedScore: Math.round(data.weightedScore * 100) / 100,
            weightedTotal: Math.round(data.weightedTotal * 100) / 100,
            percentage: Math.round(percentage * 100) / 100,
            grade: calculateGrade(percentage),
          };
        });

        // Step 2: Apply subject groups with best-of-N
        const groupResults: ComputedStudentReport['groupResults'] = [];
        const groupedSubjectNames = new Set(subjectGroups.flatMap(g => g.subjects));

        for (const group of subjectGroups) {
          const groupSubjects = group.subjects
            .map(sn => {
              const found = subjectTotals.find(st => st.subjectName === sn);
              return found
                ? { subjectName: sn, weightedScore: found.weightedScore, weightedTotal: found.weightedTotal, selected: true }
                : { subjectName: sn, weightedScore: 0, weightedTotal: 0, selected: true };
            })
            .sort((a, b) => {
              // Sort by percentage descending for best-of selection
              const pctA = a.weightedTotal > 0 ? (a.weightedScore / a.weightedTotal) * 100 : 0;
              const pctB = b.weightedTotal > 0 ? (b.weightedScore / b.weightedTotal) * 100 : 0;
              return pctB - pctA;
            });

          // Apply best-of-N
          const bestOfCount = group.bestOf > 0 ? Math.min(group.bestOf, groupSubjects.length) : groupSubjects.length;
          groupSubjects.forEach((s, idx) => {
            s.selected = idx < bestOfCount;
          });

          const selectedSubjects = groupSubjects.filter(s => s.selected);
          const totalScore = selectedSubjects.reduce((sum, s) => sum + s.weightedScore, 0);
          const totalMax = selectedSubjects.reduce((sum, s) => sum + s.weightedTotal, 0);
          const percentage = totalMax > 0 ? (totalScore / totalMax) * 100 : 0;

          groupResults.push({
            groupName: group.groupName,
            displayName: group.displayName,
            subjects: groupSubjects,
            bestOf: group.bestOf,
            totalScore: Math.round(totalScore * 100) / 100,
            totalMax: Math.round(totalMax * 100) / 100,
            percentage: Math.round(percentage * 100) / 100,
            grade: calculateGrade(percentage),
          });
        }

        // Ungrouped subjects as individual entries in grand total
        const ungrouped = subjectTotals.filter(st => !groupedSubjectNames.has(st.subjectName));

        // Grand total: groups + ungrouped
        let grandTotal = 0;
        let grandMax = 0;

        groupResults.forEach(gr => {
          grandTotal += gr.totalScore;
          grandMax += gr.totalMax;
        });
        ungrouped.forEach(s => {
          grandTotal += s.weightedScore;
          grandMax += s.weightedTotal;
        });

        const grandPercentage = grandMax > 0 ? (grandTotal / grandMax) * 100 : 0;

        // Check if student has failed any subject (below passing marks)
        let hasFailed = false;
        subjectTotals.forEach(st => {
          const subjectConfig = subjects.find(s => s.subjectName === st.subjectName);
          const passingThreshold = subjectConfig ? (subjectConfig.passingMarks / subjectConfig.totalMarks) * 100 : 33;
          if (st.percentage < passingThreshold) hasFailed = true;
        });

        // Exam-wise results for the detail view
        const examResultsArr = selectedExams.map(examId => {
          const exam = exams.find(e => e.examId === examId);
          return {
            examId,
            examName: exam?.examName || examId,
            subjects: Object.fromEntries(
              Object.entries(student.examResults.get(examId) || {})
            ),
          };
        });

        computedReports.push({
          studentId: student.studentId,
          studentName: student.studentName,
          rollNo: student.rollNo,
          section: student.section,
          examResults: examResultsArr,
          subjectTotals,
          groupResults,
          grandTotal: Math.round(grandTotal * 100) / 100,
          grandMax: Math.round(grandMax * 100) / 100,
          grandPercentage: Math.round(grandPercentage * 100) / 100,
          overallGrade: calculateGrade(grandPercentage),
          rank: 0,
          result: hasFailed ? 'Fail' : 'Pass',
        });
      });

      // Calculate ranks by grandTotal descending
      computedReports.sort((a, b) => b.grandTotal - a.grandTotal);
      computedReports.forEach((r, idx) => { r.rank = idx + 1; });

      setStudentReports(computedReports);
      setViewMode('preview');
    } catch (error) {
      console.error('Error computing reports:', error);
      alert('Failed to compute report cards.');
    } finally {
      setComputing(false);
    }
  }, [selectedExams, examWeights, subjectGroups, domain, section, subjects, exams]);

  // Publish report cards to Firestore
  const publishReportCards = async () => {
    if (studentReports.length === 0) return;
    if (!confirm(`Publish report cards for ${studentReports.length} students in Class ${className}-${section}?`)) return;

    setPublishing(true);
    try {
      for (const report of studentReports) {
        const docId = `${className}_${section}_${report.studentId}_${academicYear.replace('-', '_')}`;
        await setDoc(doc(db, `school-data/${domain}/report-cards`, docId), {
          ...report,
          className,
          section,
          academicYear,
          domain,
          publishedAt: new Date().toISOString(),
          published: true,
        });
      }

      // Update config as published
      if (savedConfig) {
        const configId = `rc_${className}_${academicYear.replace('-', '_')}`;
        await setDoc(doc(db, `school-data/${domain}/report-card-configs`, configId), {
          ...savedConfig,
          published: true,
        });
        setSavedConfig({ ...savedConfig, published: true });
      }

      alert(`Report cards published for ${studentReports.length} students!`);
    } catch (error: any) {
      console.error('Error publishing:', error);
      alert(`Failed to publish: ${error.message}`);
    } finally {
      setPublishing(false);
    }
  };

  const getGradeColor = (grade: string): string => {
    const colors: Record<string, string> = {
      'A+': '#28A745', 'A': '#20C997', 'B+': '#17A2B8', 'B': '#2D7DD2',
      'C+': '#FFC107', 'C': '#FB8C00', 'D': '#E83E8C', 'F': '#DC3545',
    };
    return colors[grade] || '#6c757d';
  };

  return (
    <div>
      {/* Header Card */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">
                <i className="bi bi-file-earmark-ruled me-2 text-primary-custom"></i>
                Report Card Generator
              </h5>
              <small className="text-muted">Combine exam results, club subjects, apply best-of-N rules</small>
            </div>
            <div className="btn-group">
              <button
                className={`btn btn-sm ${viewMode === 'config' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setViewMode('config')}
                style={viewMode === 'config' ? { backgroundColor: '#5C2597', borderColor: '#5C2597' } : {}}
              >
                <i className="bi bi-gear me-1"></i>Configure
              </button>
              <button
                className={`btn btn-sm ${viewMode === 'preview' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => { if (studentReports.length > 0) setViewMode('preview'); }}
                disabled={studentReports.length === 0}
                style={viewMode === 'preview' ? { backgroundColor: '#5C2597', borderColor: '#5C2597' } : {}}
              >
                <i className="bi bi-eye me-1"></i>Preview ({studentReports.length})
              </button>
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label small fw-semibold text-muted">Class</label>
              <select className="form-select form-select-sm" value={className} onChange={e => setClassName(e.target.value)}>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={String(i + 1)}>Class {i + 1}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-semibold text-muted">Section</label>
              <select className="form-select form-select-sm" value={section} onChange={e => setSection(e.target.value)}>
                {['A', 'B', 'C', 'D', 'E'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-semibold text-muted">Academic Year</label>
              <select className="form-select form-select-sm" value={academicYear} onChange={e => setAcademicYear(e.target.value)}>
                {academicYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="col-md-3 d-flex align-items-end">
              {loading && <span className="spinner-border spinner-border-sm text-primary"></span>}
              {!loading && (
                <small className="text-muted">
                  {exams.length} exam{exams.length !== 1 ? 's' : ''} found, {subjects.length} subject{subjects.length !== 1 ? 's' : ''}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'config' && (
        <>
          {/* Step 1: Select Exams & Weights */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <h6 className="mb-0">
                <span className="badge bg-primary me-2" style={{ backgroundColor: '#5C2597' }}>1</span>
                Select Exams & Weightage
              </h6>
              <small className="text-muted">Choose which exams contribute to the report card and their weights</small>
            </div>
            <div className="card-body">
              {exams.length === 0 ? (
                <div className="alert alert-warning mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  No exams found for Class {className}-{section} ({academicYear}). Upload exam results first in the "Results" tab.
                </div>
              ) : (
                <div className="row g-3">
                  {exams.map(exam => (
                    <div key={exam.examId} className="col-md-6">
                      <div className={`card h-100 ${selectedExams.includes(exam.examId) ? 'border-primary' : ''}`}
                        style={selectedExams.includes(exam.examId) ? { borderColor: '#5C2597', borderWidth: '2px' } : {}}
                      >
                        <div className="card-body p-3">
                          <div className="form-check d-flex align-items-start">
                            <input
                              className="form-check-input mt-1 me-3"
                              type="checkbox"
                              checked={selectedExams.includes(exam.examId)}
                              onChange={() => toggleExam(exam.examId)}
                              id={`exam-${exam.examId}`}
                            />
                            <label className="form-check-label flex-grow-1" htmlFor={`exam-${exam.examId}`}>
                              <div className="fw-semibold">{exam.examName}</div>
                              <div className="text-muted small">
                                {exam.examType} | {exam.subjects?.length || 0} subjects
                                {exam.published && <span className="badge bg-success ms-2" style={{ fontSize: '0.6rem' }}>Published</span>}
                              </div>
                            </label>
                          </div>
                          {selectedExams.includes(exam.examId) && (
                            <div className="mt-2 ps-4">
                              <label className="form-label small text-muted mb-1">Weight</label>
                              <div className="input-group input-group-sm" style={{ maxWidth: '160px' }}>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={examWeights[exam.examId] || 100}
                                  onChange={e => setExamWeights(prev => ({ ...prev, [exam.examId]: Number(e.target.value) }))}
                                  min={1}
                                  max={100}
                                />
                                <span className="input-group-text">%</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Step 2: Subject Groups */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0">
                    <span className="badge bg-primary me-2" style={{ backgroundColor: '#5C2597' }}>2</span>
                    Subject Groups & Best-of-N Rules
                  </h6>
                  <small className="text-muted">Club subjects together and pick best-of-N scores</small>
                </div>
                <button className="btn btn-sm btn-outline-primary" onClick={() => openGroupModal()}>
                  <i className="bi bi-plus-circle me-1"></i>Add Group
                </button>
              </div>
            </div>
            <div className="card-body">
              {subjectGroups.length === 0 && ungroupedSubjects.length > 0 && (
                <div className="alert alert-info mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  No groups created yet. All {ungroupedSubjects.length} subjects will be treated individually.
                  Create a group to club subjects and apply best-of-N rules.
                </div>
              )}

              {/* Existing Groups */}
              {subjectGroups.map(group => (
                <div key={group.id} className="card mb-3" style={{ borderLeft: '4px solid #5C2597' }}>
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="fw-bold">{group.displayName || group.groupName}</div>
                        <div className="text-muted small">
                          {group.subjects.length} subjects
                          {group.bestOf > 0 && (
                            <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.65rem' }}>
                              Best of {group.bestOf}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-secondary" onClick={() => openGroupModal(group)}>
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className="btn btn-outline-danger" onClick={() => deleteGroup(group.id)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {group.subjects.map(s => (
                        <span key={s} className="badge bg-light text-dark border" style={{ fontSize: '0.7rem' }}>{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Ungrouped subjects */}
              {ungroupedSubjects.length > 0 && (
                <div className="card" style={{ borderLeft: '4px solid #6c757d' }}>
                  <div className="card-body p-3">
                    <div className="fw-semibold text-muted small mb-2">
                      <i className="bi bi-dash-circle me-1"></i>
                      Ungrouped Subjects (counted individually)
                    </div>
                    <div className="d-flex flex-wrap gap-1">
                      {ungroupedSubjects.map(s => (
                        <span key={s} className="badge bg-light text-dark border" style={{ fontSize: '0.7rem' }}>{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body d-flex gap-3 flex-wrap">
              <button className="btn btn-outline-primary" onClick={saveConfig} disabled={selectedExams.length === 0}>
                <i className="bi bi-save me-2"></i>Save Configuration
              </button>
              <button
                className="btn btn-primary"
                onClick={computeReports}
                disabled={selectedExams.length === 0 || computing}
                style={{ backgroundColor: '#5C2597', borderColor: '#5C2597' }}
              >
                {computing ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span>Computing...</>
                ) : (
                  <><i className="bi bi-calculator me-2"></i>Generate Report Cards</>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Preview Mode */}
      {viewMode === 'preview' && studentReports.length > 0 && (
        <>
          {/* Summary Stats */}
          <div className="row g-3 mb-4">
            {[
              { label: 'Students', value: studentReports.length, icon: 'bi-people', color: '#5C2597' },
              { label: 'Class Average', value: `${(studentReports.reduce((s, r) => s + r.grandPercentage, 0) / studentReports.length).toFixed(1)}%`, icon: 'bi-bar-chart', color: '#2D7DD2' },
              { label: 'Passed', value: studentReports.filter(r => r.result === 'Pass').length, icon: 'bi-check-circle', color: '#28A745' },
              { label: 'Failed', value: studentReports.filter(r => r.result === 'Fail').length, icon: 'bi-x-circle', color: '#DC3545' },
              { label: 'Topper', value: studentReports[0]?.studentName?.split(' ')[0] || '-', icon: 'bi-trophy', color: '#FFC107' },
            ].map((stat, idx) => (
              <div key={idx} className="col-6 col-md">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body d-flex align-items-center p-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                      style={{ width: '40px', height: '40px', backgroundColor: `${stat.color}15`, color: stat.color }}>
                      <i className={`bi ${stat.icon}`}></i>
                    </div>
                    <div>
                      <div className="text-muted" style={{ fontSize: '0.7rem' }}>{stat.label}</div>
                      <div className="fw-bold">{stat.value}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Publish Button */}
          <div className="d-flex gap-2 mb-4">
            <button
              className="btn btn-success"
              onClick={publishReportCards}
              disabled={publishing}
            >
              {publishing ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Publishing...</>
              ) : (
                <><i className="bi bi-send me-2"></i>Publish Report Cards</>
              )}
            </button>
            <button className="btn btn-outline-secondary" onClick={() => setViewMode('config')}>
              <i className="bi bi-arrow-left me-1"></i>Back to Config
            </button>
          </div>

          {/* Student Report Cards */}
          <div className="row g-3">
            {studentReports.map(report => {
              const isExpanded = expandedStudent === report.studentId;
              return (
                <div key={report.studentId} className="col-12">
                  <div className="card border-0 shadow-sm">
                    {/* Summary Row */}
                    <div
                      className="card-body p-3"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedStudent(isExpanded ? null : report.studentId)}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3 fw-bold flex-shrink-0"
                            style={{ width: '40px', height: '40px', backgroundColor: `${getGradeColor(report.overallGrade)}15`, color: getGradeColor(report.overallGrade), fontSize: '0.85rem' }}
                          >
                            #{report.rank}
                          </div>
                          <div>
                            <div className="fw-bold">{report.studentName}</div>
                            <div className="text-muted small">Roll No: {report.rollNo} | ID: {report.studentId}</div>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <div className="text-center">
                            <div className="fw-bold">{report.grandTotal}/{report.grandMax}</div>
                            <div className="text-muted" style={{ fontSize: '0.65rem' }}>Total</div>
                          </div>
                          <div className="text-center">
                            <div className="fw-bold" style={{ color: getGradeColor(report.overallGrade) }}>
                              {report.grandPercentage}%
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.65rem' }}>Percentage</div>
                          </div>
                          <span className="badge text-white px-2 py-1" style={{ backgroundColor: getGradeColor(report.overallGrade), fontSize: '0.8rem' }}>
                            {report.overallGrade}
                          </span>
                          <span className={`badge ${report.result === 'Pass' ? 'bg-success' : 'bg-danger'}`}>
                            {report.result}
                          </span>
                          <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <div className="card-body pt-0 border-top">
                        {/* Exam-wise breakdown */}
                        {report.examResults.length > 1 && (
                          <div className="mb-3 mt-3">
                            <h6 className="small fw-bold text-muted mb-2">
                              <i className="bi bi-journal-check me-1"></i>Exam-wise Marks
                            </h6>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.8rem' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th>Subject</th>
                                    {report.examResults.map(er => (
                                      <th key={er.examId} className="text-center">{er.examName.split(' - ')[0]}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {report.subjectTotals.map(st => (
                                    <tr key={st.subjectName}>
                                      <td className="fw-semibold">{st.subjectName}</td>
                                      {report.examResults.map(er => {
                                        const marks = er.subjects[st.subjectName];
                                        return (
                                          <td key={er.examId} className="text-center">
                                            {marks ? `${marks.scored}/${marks.total}` : '-'}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Groups with best-of */}
                        {report.groupResults.length > 0 && (
                          <div className="mb-3">
                            <h6 className="small fw-bold text-muted mb-2">
                              <i className="bi bi-collection me-1"></i>Subject Group Results
                            </h6>
                            {report.groupResults.map(gr => (
                              <div key={gr.groupName} className="card mb-2" style={{ borderLeft: `3px solid ${getGradeColor(gr.grade)}` }}>
                                <div className="card-body p-2 px-3">
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <div className="fw-semibold small">{gr.displayName}</div>
                                    <div className="d-flex align-items-center gap-2">
                                      {gr.bestOf > 0 && (
                                        <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>
                                          Best of {gr.bestOf}
                                        </span>
                                      )}
                                      <span className="fw-bold small">{gr.totalScore}/{gr.totalMax}</span>
                                      <span className="badge text-white" style={{ backgroundColor: getGradeColor(gr.grade), fontSize: '0.65rem' }}>
                                        {gr.grade} ({gr.percentage}%)
                                      </span>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-wrap gap-1">
                                    {gr.subjects.map(s => (
                                      <span
                                        key={s.subjectName}
                                        className={`badge ${s.selected ? 'bg-light text-dark border' : 'bg-light text-muted border'}`}
                                        style={{ fontSize: '0.65rem', opacity: s.selected ? 1 : 0.5, textDecoration: s.selected ? 'none' : 'line-through' }}
                                      >
                                        {s.subjectName}: {s.weightedScore}/{s.weightedTotal}
                                        {!s.selected && ' (dropped)'}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Weighted Subject Totals */}
                        <h6 className="small fw-bold text-muted mb-2">
                          <i className="bi bi-list-ol me-1"></i>Weighted Subject Totals
                        </h6>
                        <div className="table-responsive">
                          <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.8rem' }}>
                            <thead className="table-light">
                              <tr>
                                <th>Subject</th>
                                <th className="text-center">Score</th>
                                <th className="text-center">Total</th>
                                <th className="text-center">%</th>
                                <th className="text-center">Grade</th>
                              </tr>
                            </thead>
                            <tbody>
                              {report.subjectTotals.map(st => (
                                <tr key={st.subjectName}>
                                  <td>{st.subjectName}</td>
                                  <td className="text-center">{st.weightedScore}</td>
                                  <td className="text-center">{st.weightedTotal}</td>
                                  <td className="text-center">{st.percentage}%</td>
                                  <td className="text-center">
                                    <span className="badge text-white" style={{ backgroundColor: getGradeColor(st.grade), fontSize: '0.7rem' }}>
                                      {st.grade}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                              <tr className="table-dark">
                                <td className="fw-bold">Grand Total</td>
                                <td className="text-center fw-bold">{report.grandTotal}</td>
                                <td className="text-center fw-bold">{report.grandMax}</td>
                                <td className="text-center fw-bold">{report.grandPercentage}%</td>
                                <td className="text-center">
                                  <span className="badge text-white" style={{ backgroundColor: getGradeColor(report.overallGrade) }}>
                                    {report.overallGrade}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Subject Group Modal */}
      {showGroupModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowGroupModal(false)}>
          <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-collection me-2"></i>
                  {editingGroup ? 'Edit' : 'Create'} Subject Group
                </h5>
                <button className="btn-close" onClick={() => setShowGroupModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Group Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={groupForm.groupName}
                      onChange={e => setGroupForm(prev => ({ ...prev, groupName: e.target.value }))}
                      placeholder="e.g., Sciences, Languages"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Display Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={groupForm.displayName}
                      onChange={e => setGroupForm(prev => ({ ...prev, displayName: e.target.value }))}
                      placeholder="e.g., Science Group (Best of 3)"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Best Of</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={groupForm.bestOf}
                        onChange={e => setGroupForm(prev => ({ ...prev, bestOf: Math.max(0, Number(e.target.value)) }))}
                        min={0}
                        max={groupForm.subjects.length || 20}
                      />
                      <span className="input-group-text">
                        {groupForm.bestOf === 0 ? 'All subjects' : `of ${groupForm.subjects.length}`}
                      </span>
                    </div>
                    <small className="text-muted">Set to 0 to include all subjects in this group</small>
                  </div>
                </div>

                <hr />
                <label className="form-label small fw-semibold">Select Subjects for this Group</label>

                {/* Available subjects (not in other groups) */}
                <div className="row g-2">
                  {allSubjectNames.map(subjectName => {
                    const inOtherGroup = subjectGroups.some(g => g.id !== editingGroup?.id && g.subjects.includes(subjectName));
                    const isSelected = groupForm.subjects.includes(subjectName);
                    return (
                      <div key={subjectName} className="col-md-6">
                        <div
                          className={`form-check p-2 rounded border ${isSelected ? 'border-primary bg-light' : ''}`}
                          style={isSelected ? { borderColor: '#5C2597' } : inOtherGroup ? { opacity: 0.4 } : {}}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={isSelected}
                            disabled={inOtherGroup}
                            onChange={() => toggleGroupSubject(subjectName)}
                            id={`grp-subj-${subjectName}`}
                          />
                          <label className="form-check-label small" htmlFor={`grp-subj-${subjectName}`}>
                            {subjectName}
                            {inOtherGroup && <span className="text-muted ms-1">(in another group)</span>}
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowGroupModal(false)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={saveGroup}
                  disabled={!groupForm.groupName.trim() || groupForm.subjects.length === 0}
                  style={{ backgroundColor: '#5C2597', borderColor: '#5C2597' }}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  {editingGroup ? 'Update' : 'Create'} Group
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportCard;
